import { useState, useCallback, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { assistantErrorMessage } from "@/lib/assistantError";
import { useAuth } from "@/hooks/useAuth";

export interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface AskAssistantResponse {
  success: boolean;
  data?: { response: string };
  error?: string;
}

const askAssistantFn = httpsCallable<unknown, AskAssistantResponse>(
  functions,
  "askAssistant"
);

export function useAssistant() {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { pathname } = useLocation();
  const params = useParams();
  const { orgUser } = useAuth();

  // Build ambient context
  const context = useMemo(() => {
    const studioId = params.studioId || null;
    return {
      currentPath: pathname,
      pageLabel: "",
      studioId,
      studioName: null as string | null,
      orgName: null as string | null,
    };
  }, [pathname, params.studioId]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;

      const userMsg: AssistantMessage = {
        id: crypto.randomUUID(),
        role: "user",
        content: text.trim(),
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);
      setError(null);

      // Report a failure in the transcript itself, so the reason is visible
      // where the user is already looking rather than collapsed into a
      // one-size-fits-all "couldn't connect".
      const reportFailure = (reason: string) => {
        setError(reason);
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: reason,
            timestamp: new Date(),
          },
        ]);
      };

      // Build conversation history from existing messages (last 20)
      const conversationHistory = messages.slice(-20).map((m) => ({
        role: m.role,
        content: m.content,
      }));

      try {
        const result = await askAssistantFn({
          message: text.trim(),
          conversationHistory,
          context,
        });

        const responseData = result.data;
        if (responseData.success && responseData.data) {
          const assistantMsg: AssistantMessage = {
            id: crypto.randomUUID(),
            role: "assistant",
            content: responseData.data.response,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, assistantMsg]);
        } else {
          reportFailure(
            responseData.error || "Something went wrong reaching the assistant."
          );
        }
      } catch (err) {
        console.error("Assistant error:", err);
        reportFailure(assistantErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, context]
  );

  const clearConversation = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    isOpen,
    setIsOpen,
    sendMessage,
    clearConversation,
    orgUser,
  };
}
